/**
 * Multipart Upload Engine
 *
 * Orchestrates large file uploads using S3 presigned multipart upload URLs.
 * Uses XMLHttpRequest for per-part progress tracking, parallel uploads with
 * configurable concurrency, and automatic retry with exponential backoff.
 */

import { customerApiClient } from './api';
import type { UploadPart } from '@shared/types';

// ---------------------------------------------------------------------------
// Public interfaces
// ---------------------------------------------------------------------------

export interface UploadProgress {
  loaded: number;
  total: number;
  speed: number; // bytes per second
  eta: number; // seconds remaining
  percent: number; // 0-100
}

export interface UploadEngineOptions {
  concurrency?: number; // default 4
  retries?: number; // default 3
  onProgress?: (uploadId: string, progress: UploadProgress) => void;
  onComplete?: (uploadId: string) => void;
  onError?: (uploadId: string, error: Error) => void;
}

export type UploadStatus = 'pending' | 'uploading' | 'complete' | 'failed' | 'aborted';

export interface ActiveUpload {
  id: string;
  projectId: string;
  file: File;
  uploadSk: string;
  status: UploadStatus;
  progress: UploadProgress;
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface PartState {
  partNumber: number;
  start: number;
  end: number;
  loaded: number;
  etag: string | null;
  retries: number;
  xhr: XMLHttpRequest | null;
}

interface UploadState {
  id: string;
  projectId: string;
  file: File;
  uploadSk: string;
  s3UploadId: string;
  partSize: number;
  totalParts: number;
  parts: PartState[];
  completedParts: UploadPart[];
  status: UploadStatus;
  abortController: AbortController;
  startTime: number;
  lastProgressTime: number;
  lastProgressLoaded: number;
}

// ---------------------------------------------------------------------------
// MultipartUploadEngine
// ---------------------------------------------------------------------------

export class MultipartUploadEngine {
  private concurrency: number;
  private retries: number;
  private onProgress?: UploadEngineOptions['onProgress'];
  private onComplete?: UploadEngineOptions['onComplete'];
  private onError?: UploadEngineOptions['onError'];

  private uploads = new Map<string, UploadState>();
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  constructor(options: UploadEngineOptions = {}) {
    this.concurrency = options.concurrency ?? 4;
    this.retries = options.retries ?? 3;
    this.onProgress = options.onProgress;
    this.onComplete = options.onComplete;
    this.onError = options.onError;

    this.setupBeforeUnload();
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Begin uploading a file. Returns an internal upload ID used to track
   * and cancel the upload.
   */
  async uploadFile(projectId: string, file: File): Promise<string> {
    // 1. Initiate multipart upload on backend
    const initResponse = await customerApiClient.initiateUpload(projectId, {
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || 'application/octet-stream',
    });

    const { upload_id, upload_sk, part_size, total_parts } = initResponse;

    // Build part states
    const parts: PartState[] = [];
    for (let i = 0; i < total_parts; i++) {
      const start = i * part_size;
      const end = Math.min(start + part_size, file.size);
      parts.push({
        partNumber: i + 1,
        start,
        end,
        loaded: 0,
        etag: null,
        retries: 0,
        xhr: null,
      });
    }

    const state: UploadState = {
      id: upload_id,
      projectId,
      file,
      uploadSk: upload_sk,
      s3UploadId: initResponse.upload_id_s3,
      partSize: part_size,
      totalParts: total_parts,
      parts,
      completedParts: [],
      status: 'uploading',
      abortController: new AbortController(),
      startTime: Date.now(),
      lastProgressTime: Date.now(),
      lastProgressLoaded: 0,
    };

    this.uploads.set(upload_id, state);
    this.emitProgress(state);

    // 2. Start the upload pipeline
    this.processUpload(state).catch((err) => {
      if (state.status !== 'aborted') {
        state.status = 'failed';
        this.onError?.(state.id, err instanceof Error ? err : new Error(String(err)));
      }
    });

    return upload_id;
  }

  /**
   * Cancel an in-progress upload.
   */
  async cancelUpload(uploadId: string): Promise<void> {
    const state = this.uploads.get(uploadId);
    if (!state || state.status !== 'uploading') return;

    state.status = 'aborted';
    state.abortController.abort();

    // Abort any in-flight XHRs
    for (const part of state.parts) {
      if (part.xhr) {
        part.xhr.abort();
        part.xhr = null;
      }
    }

    // Notify backend to abort the multipart upload
    try {
      await customerApiClient.abortUpload(state.projectId, state.uploadSk);
    } catch {
      // Best-effort abort; S3 lifecycle rules will clean up incomplete uploads
    }

    this.emitProgress(state);
  }

  /**
   * Get a snapshot of all active uploads.
   */
  getActiveUploads(): ActiveUpload[] {
    const result: ActiveUpload[] = [];
    for (const state of this.uploads.values()) {
      result.push({
        id: state.id,
        projectId: state.projectId,
        file: state.file,
        uploadSk: state.uploadSk,
        status: state.status,
        progress: this.calculateProgress(state),
      });
    }
    return result;
  }

  /**
   * Check whether any uploads are still in progress.
   */
  hasActiveUploads(): boolean {
    for (const state of this.uploads.values()) {
      if (state.status === 'uploading') return true;
    }
    return false;
  }

  /**
   * Remove completed/failed/aborted uploads from the internal map.
   */
  clearFinished(): void {
    for (const [id, state] of this.uploads.entries()) {
      if (state.status !== 'uploading' && state.status !== 'pending') {
        this.uploads.delete(id);
      }
    }
  }

  /**
   * Tear down the engine (remove event listeners).
   */
  destroy(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }

  // -------------------------------------------------------------------------
  // Upload pipeline
  // -------------------------------------------------------------------------

  private async processUpload(state: UploadState): Promise<void> {
    const pendingParts = [...state.parts];
    const batchSize = this.concurrency * 2;

    while (pendingParts.length > 0 && state.status === 'uploading') {
      // Determine which parts still need presigned URLs
      const batch = pendingParts.splice(0, batchSize);
      const partNumbers = batch.map((p) => p.partNumber);

      // Request presigned URLs for this batch
      const { urls } = await customerApiClient.presignParts(
        state.projectId,
        state.uploadSk,
        partNumbers,
      );

      // Build a map of part number -> presigned URL
      const urlMap = new Map(urls.map((u) => [u.part_number, u.url]));

      // Upload parts in parallel with concurrency limit
      await this.uploadPartsWithConcurrency(state, batch, urlMap);

      if (state.status !== 'uploading') return;
    }

    if (state.status !== 'uploading') return;

    // All parts uploaded — complete the multipart upload
    const sortedParts = state.completedParts
      .slice()
      .sort((a, b) => a.PartNumber - b.PartNumber);

    await customerApiClient.completeUpload(
      state.projectId,
      state.uploadSk,
      sortedParts,
    );

    state.status = 'complete';
    this.emitProgress(state);
    this.onComplete?.(state.id);
  }

  private async uploadPartsWithConcurrency(
    state: UploadState,
    parts: PartState[],
    urlMap: Map<number, string>,
  ): Promise<void> {
    const queue = [...parts];
    const inFlight = new Set<Promise<void>>();

    const processNext = async (): Promise<void> => {
      while (queue.length > 0 && state.status === 'uploading') {
        const part = queue.shift()!;
        const url = urlMap.get(part.partNumber);
        if (!url) {
          throw new Error(`No presigned URL for part ${part.partNumber}`);
        }

        const promise = this.uploadPart(state, part, url).then(() => {
          inFlight.delete(promise);
        });
        inFlight.add(promise);

        if (inFlight.size >= this.concurrency) {
          await Promise.race(inFlight);
        }
      }
    };

    await processNext();
    // Wait for all remaining in-flight uploads
    await Promise.all(inFlight);
  }

  private uploadPart(
    state: UploadState,
    part: PartState,
    url: string,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (state.status !== 'uploading') {
        resolve();
        return;
      }

      const blob = state.file.slice(part.start, part.end);
      const xhr = new XMLHttpRequest();
      part.xhr = xhr;

      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          part.loaded = event.loaded;
          this.emitProgress(state);
        }
      };

      xhr.onload = () => {
        part.xhr = null;
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag');
          if (!etag) {
            reject(new Error(`No ETag returned for part ${part.partNumber}`));
            return;
          }
          part.etag = etag;
          part.loaded = part.end - part.start;
          state.completedParts.push({
            PartNumber: part.partNumber,
            ETag: etag,
          });
          this.emitProgress(state);
          resolve();
        } else {
          this.handlePartError(state, part, url, xhr.status, resolve, reject);
        }
      };

      xhr.onerror = () => {
        part.xhr = null;
        this.handlePartError(state, part, url, 0, resolve, reject);
      };

      xhr.onabort = () => {
        part.xhr = null;
        resolve();
      };

      // Listen for abort signal
      state.abortController.signal.addEventListener('abort', () => {
        xhr.abort();
      });

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', state.file.type || 'application/octet-stream');
      xhr.send(blob);
    });
  }

  private handlePartError(
    state: UploadState,
    part: PartState,
    url: string,
    _statusCode: number,
    resolve: () => void,
    reject: (err: Error) => void,
  ): void {
    if (state.status !== 'uploading') {
      resolve();
      return;
    }

    part.retries += 1;
    if (part.retries <= this.retries) {
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, part.retries - 1) * 1000;
      part.loaded = 0;
      setTimeout(() => {
        if (state.status !== 'uploading') {
          resolve();
          return;
        }
        this.uploadPart(state, part, url).then(resolve).catch(reject);
      }, delay);
    } else {
      reject(
        new Error(
          `Part ${part.partNumber} failed after ${this.retries} retries`,
        ),
      );
    }
  }

  // -------------------------------------------------------------------------
  // Progress tracking
  // -------------------------------------------------------------------------

  private calculateProgress(state: UploadState): UploadProgress {
    const total = state.file.size;
    let loaded = 0;

    for (const part of state.parts) {
      if (part.etag) {
        // Part fully uploaded
        loaded += part.end - part.start;
      } else {
        loaded += part.loaded;
      }
    }

    const elapsed = (Date.now() - state.startTime) / 1000;
    const speed = elapsed > 0 ? loaded / elapsed : 0;
    const remaining = total - loaded;
    const eta = speed > 0 ? remaining / speed : 0;
    const percent = total > 0 ? Math.min(100, (loaded / total) * 100) : 0;

    return { loaded, total, speed, eta, percent };
  }

  private emitProgress(state: UploadState): void {
    const progress = this.calculateProgress(state);
    this.onProgress?.(state.id, progress);
  }

  // -------------------------------------------------------------------------
  // Beforeunload guard
  // -------------------------------------------------------------------------

  private setupBeforeUnload(): void {
    this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (this.hasActiveUploads()) {
        e.preventDefault();
        // Modern browsers ignore custom messages but still show a generic prompt
        e.returnValue = 'Uploads are still in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }
}
